import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useUser } from "@clerk/tanstack-react-start";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { UpgradePlanModal } from "@/components/billing/UpgradePlanModal";
import { PurchaseCreditsModal } from "@/components/billing/PurchaseCreditsModal";
import { routeAuthCheck, loadOnboardingStatusForContext } from "@/server/route-guard";
import { executeStateMachineRedirect } from "@/lib/routing/onboarding-redirect";
import { logWarn, logInfo } from "@/lib/structured-log";
import type {
  AgentChatSessionResponse,
  OnboardingStatusResponse,
  UserWorkspaceItem,
  SubscriptionResponse,
  CreditsSummary,
} from "@/lib/api/generated";
import { toSubscriptionResponseStatus } from "@/lib/billing/subscription-status";
import { getUserWorkspacesServer, updateLastWorkspaceServer } from "@/server/user-workspaces";
import { getCredits, getSubscription } from "@/server/billing";
import { identifyUserForIntercom } from "@/lib/user-tracking";
import { Trans, useTranslation } from "react-i18next";
import { getUserProfileServer } from "@/server/users";
import { listAgentChatSessionsServer } from "@/server/agent-sessions";
import { WorkspaceUserProvider } from "@/components/workspace/workspace-user-context";

/**
 * Loader data returned by workspace layout route
 *
 * This data is cached and available to all child routes via context.loaderData,
 * allowing child routes to access workspace information without making duplicate API calls.
 */
export interface WorkspaceLayoutLoaderData {
  /** Complete onboarding status from backend - cached for child routes */
  onboardingStatus: OnboardingStatusResponse;
  /** Workspace ID extracted from onboarding status */
  workspaceId: string;
  /** Workspace slug for URL routing */
  workspaceSlug: string | null;
  /** Workspace display name */
  workspaceName: string | null;
  /** Current user's display name */
  userDisplayName: string | null;
  userLanguage: string | null;
  /** All workspaces accessible by the current user */
  userWorkspaces: UserWorkspaceItem[];
  /** Billing summary for sidebar and global banner */
  billingSummary: {
    subscription: SubscriptionResponse | null;
    credits: CreditsSummary | null;
  } | null;
  trackingProfile: {
    phoneNumber: string | null;
    companyWebsite: string | null;
    linkedinUrl: string | null;
    whatsapp: string | null;
    location: string | null;
    referralSource: string | null;
    createdAt: number | null;
  } | null;
  agentSessions: AgentChatSessionResponse[];
}

export const Route = createFileRoute("/workspace/$workspaceSlug")({
  component: WorkspaceLayout,

  // Cache workspace data for 5 minutes (session-scoped data)
  // Aligns with root route cache strategy for static/session data
  // Provides good balance between freshness and performance
  // NOTE: TanStack Router's cache key automatically includes route params ($workspaceSlug)
  // So navigating within the same workspace uses cached data, while switching workspaces
  // triggers a new data fetch.
  staleTime: 5 * 60_000, // 5 minutes (aligns with root route)
  gcTime: 10 * 60_000, // 10 minutes until garbage collection

  // beforeLoad: Fast authentication check (~10ms)
  // Also provides workspaceId to context for child routes (TanStack Router requirement)
  // NOTE: Child routes access workspaceId via context, which only includes beforeLoad data
  beforeLoad: async ({ params }) => {
    logInfo("workspace.layout.beforeLoad", { workspaceSlug: params.workspaceSlug });
    await routeAuthCheck();

    // We need to provide minimal data for child routes via context
    // The actual data fetching happens in loader, but workspaceId needs to be in context
    // for child routes that use getWorkspaceIdWithFallback(context, ...)
    return {
      // Provide workspaceSlug so child routes can access it
      // The actual workspaceId will be resolved in loader after data fetch
      workspaceSlug: params.workspaceSlug,
    };
  },

  // 只跟踪 workspaceSlug，避免同一 workspace 内子路由切换导致 loader 频繁刷新
  loaderDeps: (opts) => {
    // TanStack Router 类型未显式暴露 params，这里通过安全断言提取 slug 作为依赖
    const workspaceSlug = (opts as { params?: { workspaceSlug?: string } }).params?.workspaceSlug;
    return { workspaceSlug: workspaceSlug ?? "" };
  },

  // loader: All data fetching happens here
  // Benefits of this approach:
  // - Loader cache is fully controlled by staleTime
  // - Same workspace navigation (5 min) uses cached data without API calls
  // - Workspace switch triggers new data fetch (different route param = different cache key)
  // - True stale-while-revalidate behavior
  loader: async ({ params, location }): Promise<WorkspaceLayoutLoaderData> => {
    // Check onboarding state FIRST — redirect early before fetching workspace data
    const { onboardingStatus } = await loadOnboardingStatusForContext();
    executeStateMachineRedirect(onboardingStatus, location.pathname, "workspace.loader");

    // Only fetch workspace data after state machine confirms user should be here
    const workspacesData = await getUserWorkspacesServer();

    const urlSlug = params.workspaceSlug;

    logInfo("workspace.loader.data_fetched", {
      workspace_slug: urlSlug,
      onboarding_state: onboardingStatus.onboarding_state,
      workspace_count: workspacesData.workspaces.length,
      available_slugs: workspacesData.workspaces.map((ws) => ws.slug),
    });

    // Match workspace from URL slug
    const currentWorkspace = workspacesData.workspaces.find((ws) => ws.slug === urlSlug);

    // If user has no access to the URL workspace, redirect to last workspace or first available
    if (!currentWorkspace && workspacesData.workspaces.length > 0) {
      const targetSlug = workspacesData.last_workspace_slug || workspacesData.workspaces[0]?.slug;

      if (targetSlug && targetSlug !== urlSlug) {
        logInfo("workspace.loader.redirect_to_last", {
          from: urlSlug,
          to: targetSlug,
          reason: "no_access_to_url_workspace",
        });
        throw redirect({
          to: "/workspace/$workspaceSlug/new-task",
          params: { workspaceSlug: targetSlug },
          search: location.search,
        });
      }
    }

    // Handle case where user doesn't have access to any workspace
    if (!currentWorkspace) {
      logWarn("workspace.loader.no_accessible_workspace", {
        urlSlug: params.workspaceSlug,
        availableWorkspaces: workspacesData.workspaces.map((ws) => ws.slug),
        message: "User does not have access to any workspace",
      });

      // Profile submission repairs missing workspace data.
      throw redirect({ to: "/onboarding/profile" });
    }

    const workspaceId = currentWorkspace.id;

    logInfo("workspace.loader.workspace_matched", {
      urlSlug: params.workspaceSlug,
      workspaceId,
      workspaceName: currentWorkspace.display_name,
    });

    // Update last workspace in background (fire-and-forget)
    // Only update if this is a different workspace than last recorded
    if (workspacesData.last_workspace_slug !== currentWorkspace.slug) {
      updateLastWorkspaceServer({ data: { workspaceId } }).catch(() => {
        // Silently ignore errors - this is a non-critical operation
      });
    }

    // Fetch billing summary safely
    let billingSummary = null;
    try {
      const [subscription, credits] = await Promise.all([
        getSubscription({ data: workspaceId }).catch(() => null),
        getCredits({ data: workspaceId }).catch(() => null),
      ]);
      billingSummary = { subscription, credits: credits ?? subscription?.credits ?? null };
    } catch {
      // Ignore billing fetch errors to prevent blocking workspace access
      logWarn("workspace.loader.billing_fetch_failed", { workspaceId });
    }

    // Fetch additional profile fields for GTM dataLayer tracking
    let trackingProfile: WorkspaceLayoutLoaderData["trackingProfile"] = null;
    let profileDisplayName: string | null = null;
    try {
      const profile = await getUserProfileServer();
      profileDisplayName =
        `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() ||
        profile.username?.trim() ||
        null;
      trackingProfile = {
        phoneNumber: profile.phone_number ?? null,
        companyWebsite: profile.company_website ?? null,
        linkedinUrl: profile.linkedin_url ?? null,
        whatsapp: profile.whatsapp ?? null,
        location: profile.location ?? null,
        referralSource: profile.referral_source ?? null,
        createdAt: profile.created_at ?? null,
      };
    } catch {
      logWarn("workspace.loader.profile_fetch_failed", { workspaceId });
    }

    let agentSessions: AgentChatSessionResponse[] = [];
    try {
      const sessionsData = await listAgentChatSessionsServer({
        data: {
          workspaceId,
          limit: 20,
        },
      });
      agentSessions = sessionsData.sessions;
    } catch {
      logWarn("workspace.loader.agent_sessions_fetch_failed", { workspaceId });
    }

    // IMPORTANT: Return complete data structure for child routes
    // Child routes access workspaceId via Route.useLoaderData() or context
    return {
      onboardingStatus,
      workspaceId,
      workspaceSlug: currentWorkspace.slug ?? params.workspaceSlug,
      workspaceName: currentWorkspace.display_name ?? null,
      userDisplayName: onboardingStatus.user_display_name ?? profileDisplayName,
      userLanguage: onboardingStatus.user_language ?? null,
      userWorkspaces: workspacesData.workspaces,
      billingSummary,
      trackingProfile,
      agentSessions,
    };
  },
});

function WorkspaceLayout() {
  const { t } = useTranslation("billing");
  const {
    workspaceName,
    userDisplayName,
    userLanguage,
    userWorkspaces,
    billingSummary,
    workspaceSlug,
    workspaceId,
    trackingProfile,
    agentSessions,
  } = Route.useLoaderData();
  const isExpired =
    billingSummary?.subscription?.status === "expired" ||
    billingSummary?.subscription?.status === "canceled";
  const hasInsufficientCredits =
    !isExpired &&
    billingSummary?.credits?.total_credits !== undefined &&
    billingSummary.credits.total_credits <= 0;
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isPurchaseCreditsModalOpen, setIsPurchaseCreditsModalOpen] = useState(false);

  // Get Clerk user data for email and phone
  const { user: clerkUser } = useUser();
  const sidebarUserName =
    userDisplayName?.trim() || clerkUser?.fullName?.trim() || clerkUser?.username?.trim() || "";

  // Send user identification to Intercom via dataLayer when workspace loads
  useEffect(() => {
    if (clerkUser && workspaceId) {
      identifyUserForIntercom({
        user_id: clerkUser.id,
        username: userDisplayName ?? clerkUser.fullName ?? clerkUser.username ?? "",
        email: clerkUser.primaryEmailAddress?.emailAddress ?? "",
        phone_number:
          trackingProfile?.phoneNumber ?? clerkUser.primaryPhoneNumber?.phoneNumber ?? "",
        company_name: workspaceName ?? "",
        company_website: trackingProfile?.companyWebsite ?? "",
        company_id: workspaceId ?? "",
        linkedin_url: trackingProfile?.linkedinUrl ?? "",
        whatsapp: trackingProfile?.whatsapp ?? "",
        location: trackingProfile?.location ?? "",
        referral_source: trackingProfile?.referralSource ?? "",
        // Use API-returned Unix timestamp (seconds); Clerk createdAt is unreliable across SDK versions
        create_at: trackingProfile?.createdAt ?? undefined,
      });
    }
  }, [clerkUser, workspaceId, userDisplayName, workspaceName, trackingProfile]);

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="group flex h-screen w-full flex-col">
        {/* Global Expired Banner */}
        {isExpired && (
          <div className="bg-destructive/10 text-destructive flex w-full items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium">
            <AlertCircle className="size-4" />
            <p>
              <Trans
                i18nKey="billing:expired_banner.message"
                components={[
                  <button
                    key="renew-now"
                    type="button"
                    className="underline font-bold hover:opacity-80"
                    onClick={() => setIsUpgradeModalOpen(true)}
                  />,
                ]}
              />
            </p>
          </div>
        )}
        {hasInsufficientCredits && (
          <div className="bg-destructive/10 text-destructive flex w-full items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium">
            <AlertCircle className="size-4" />
            <p>
              {t("credit_shortage_banner.prefix", "You're out of credits.")}{" "}
              <button
                type="button"
                className="underline font-bold hover:opacity-80"
                onClick={() => setIsUpgradeModalOpen(true)}
              >
                {t("credit_shortage_banner.upgrade", "Upgrade your plan")}
              </button>{" "}
              {t("credit_shortage_banner.separator", "or")}{" "}
              <button
                type="button"
                className="underline font-bold hover:opacity-80"
                onClick={() => setIsPurchaseCreditsModalOpen(true)}
              >
                {t("credit_shortage_banner.purchase", "purchase credits")}
              </button>{" "}
              {t("credit_shortage_banner.suffix", "to continue.")}
            </p>
          </div>
        )}
        <div className="flex flex-1 overflow-hidden">
          <AppSidebar
            userName={sidebarUserName || undefined}
            userLanguage={userLanguage ?? undefined}
            workspaceId={workspaceId}
            userWorkspaces={userWorkspaces}
            billingSummary={billingSummary}
            agentSessions={agentSessions}
            onUpgradeClick={() => setIsUpgradeModalOpen(true)}
          />
          <SidebarInset className="relative dot-pattern-bg flex flex-col">
            {/* Mobile sidebar trigger - only visible on mobile */}
            <div className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
              <SidebarTrigger />
            </div>
            <WorkspaceUserProvider displayName={sidebarUserName}>
              <div className="relative z-10 flex-1 overflow-auto">
                <Outlet />
              </div>
            </WorkspaceUserProvider>
          </SidebarInset>
        </div>
      </div>

      <UpgradePlanModal
        open={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug ?? ""}
        currentPlan={billingSummary?.subscription?.plan_type}
        subscriptionStatus={toSubscriptionResponseStatus(billingSummary?.subscription?.status)}
        defaultSelectedPlan="professional"
      />
      <PurchaseCreditsModal
        open={isPurchaseCreditsModalOpen}
        onOpenChange={setIsPurchaseCreditsModalOpen}
        workspaceId={workspaceId}
      />
    </SidebarProvider>
  );
}
